import Log, {Errors} from "@khan/logging";

// This function takes the editor element and the printed document title, and
// uses window.print() to print or save the document as a PDF.
// Turning the HTML into a string first caused some of the MathJax to not print.
export default function printContents(
    editorElement: HTMLElement,
    printedDocumentTitle: string,
) {
    // Remove any existing print containers from failed/cancelled previous
    // prints because sometimes the cleanup doesn't work.
    const existingContainer = document.getElementById(
        "coeditor-print-container",
    );
    if (existingContainer) {
        existingContainer.remove();
    }
    const existingStyle = document.getElementById("coeditor-print-style");
    if (existingStyle) {
        existingStyle.remove();
    }

    const printContainer = document.createElement("div");
    printContainer.id = "coeditor-print-container";
    printContainer.style.display = "none";

    const clonedElement = editorElement.cloneNode(true) as HTMLElement;
    stripAttribute(clonedElement, "contenteditable");
    stripAttribute(clonedElement, "data-testid");
    stripElementsMatching(clonedElement, "[data-remove-on-print]");

    printContainer.appendChild(clonedElement);

    const style = document.createElement("style");
    style.id = "coeditor-print-style";
    style.textContent = `
        @media print {
            /* Hide everything except our print container */
            body > *:not(#coeditor-print-container) {
                display: none !important;
            }

            /* Show the print container */
            #coeditor-print-container {
                display: block !important;
            }
        }
    `;

    document.head.appendChild(style);
    document.body.appendChild(printContainer);

    const originalTitle = document.title;
    document.title = printedDocumentTitle;

    try {
        window.print();
    } catch (error) {
        Log.error("Error triggering print dialog", Errors.Internal, {
            cause: error instanceof Error ? error : undefined,
        });
    } finally {
        const cleanup = () => {
            document.title = originalTitle;
            if (document.body.contains(printContainer)) {
                document.body.removeChild(printContainer);
            }
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };

        window.addEventListener("afterprint", cleanup, {once: true});
    }
}

const stripAttribute = (element: HTMLElement, attributeName: string) => {
    if (element.hasAttribute(attributeName)) {
        element.removeAttribute(attributeName);
    }

    for (const childElement of element.querySelectorAll(`[${attributeName}]`)) {
        childElement.removeAttribute(attributeName);
    }
};

const stripElementsMatching = (element: HTMLElement, selector: string) => {
    if (element.matches(selector)) {
        element.remove();
        return;
    }

    for (const childElement of element.querySelectorAll(selector)) {
        childElement.remove();
    }
};
